// Dynamically use current hostname to prevent localhost vs 127.0.0.1 domain mismatches
const getApiBaseUrl = () => {
  // In local development (CRA dev server on port 3000), proxy to Django backend on port 8000
  if (typeof window !== 'undefined' && window.location.port === '3000') {
    return `http://${window.location.hostname}:8000/api`;
  }
  // In production, always use relative path so API calls share the same origin as the page.
  // This ensures session cookies are sent correctly regardless of www vs non-www access.
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

export interface Team {
  id: string;
  name: string;
  year: number;
  group: string;
  coach1: string;
  coach2?: string;
  jerseycolor?: string;
  logo?: string;
  teamphoto?: string;
  players: Player[];
  matches: TeamMatch[];
}

export interface Player {
  id: string;
  name: string;
  team: {
    id: string;
    name: string;
    year: number;
    group: string;
  };
  year: number;
  group: string;
  position?: string;
  height?: number;
  jersey?: number;
  photo?: string;
  stats?: PlayerStats[];
}

export interface TeamMatch {
  id: string;
  match_no: number;
  date: string;
  time?: string;
  venue?: string;
  opponent: string;
  result?: string;
}

export interface PlayerStats {
  id: string;
  player: {
    id: string;
    name: string;
    jersey: number;
  };
  match?: {
    id: string;
    date: string;
    team1: string;
    team2: string;
  };
  two_pm?: number;
  two_pa?: number;
  two_pct?: number;
  three_pm?: number;
  three_pa?: number;
  three_pct?: number;
  ftm?: number;
  fta?: number;
  ft_pct?: number;
  oreb?: number;
  dreb?: number;
  reb?: number;
  ast?: number;
  to?: number;
  st?: number;
  bs?: number;
  pf?: number;
  pts?: number;
}

export interface TeamInfo {
  id: string;
  name: string;
  logo: string;
}

export interface Match {
  id: string;
  match_no: number;
  date: string;
  time?: string;
  venue?: string;
  venuelong?: string;
  group: string;
  year: number;
  team1: TeamInfo;
  team2: TeamInfo;
  score1?: number;
  score2?: number;
  // Quarter scores
  team1q1?: number;
  team1q2?: number;
  team1q3?: number;
  team1q4?: number;
  team1ot1?: number;
  team2q1?: number;
  team2q2?: number;
  team2q3?: number;
  team2q4?: number;
  team2ot1?: number;
  // YouTube links
  ytq1?: string;
  ytq2?: string;
  ytq3?: string;
  ytq4?: string;
  ytot?: string;
  // Legacy fields (keep for compatibility)
  team1_scores?: number[]; // [Q1, Q2, Q3, Q4, OT]
  team2_scores?: number[];
  youtube_links?: (string | null)[]; // One per quarter/OT
  photo_album_url?: string;
  prelim?: boolean;
  team1_stats: PlayerStats[];
  team2_stats: PlayerStats[];
}

export interface Stat {
  id: string;
  match: string;
  player: string;
  two_pm?: number;
  two_pa?: number;
  three_pm?: number;
  three_pa?: number;
  ftm?: number;
  fta?: number;
  oreb?: number;
  dreb?: number;
  reb?: number;
  ast?: number;
  to?: number;
  st?: number;
  bs?: number;
  pf?: number;
  pts?: number;
}

export interface Gallery {
  id: string;
  title: string;
  description: string;
  coverImage: string | null;
  photoCount: number | string;
  date: string;
  folderPath: string;
}

export interface GalleryResponse {
  galleries: Gallery[];
  pagination: {
    page: number;
    page_size: number;
    total_matches: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

export interface Photo {
  id: string;
  title: string;
  imageUrl: string;
  originalUrl: string;
  date: string;
  description?: string;
}

export interface PhotoPagination {
  page: number;
  page_size: number;
  total_photos: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface PhotoResponse {
  photos: Photo[];
  pagination: PhotoPagination;
}

class ApiService {
  private async request<T>(endpoint: string): Promise<T> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Teams
  async getTeams(): Promise<Team[]> {
    return this.request<Team[]>('/teams');
  }

  async getTeam(id: string): Promise<Team> {
    return this.request<Team>(`/teams/${id}`);
  }

  // Players
  async getPlayers(): Promise<Player[]> {
    return this.request<Player[]>('/players');
  }

  async getPlayer(id: string): Promise<Player> {
    return this.request<Player>(`/players/${id}`);
  }

  // Matches
  async getMatches(): Promise<Match[]> {
    return this.request<Match[]>('/matches');
  }

  async getMatch(id: string): Promise<Match> {
    return this.request<Match>(`/matches/${id}`);
  }

  async getMatchDetail(id: string): Promise<Match> {
    return this.request<Match>(`/matches/${id}`);
  }

  // Stats
  async getStats(): Promise<Stat[]> {
    return this.request<Stat[]>('/stats');
  }

  async getMatchStats(matchId: string): Promise<Stat[]> {
    return this.request<Stat[]>(`/matches/${matchId}/stats`);
  }

  async getStatisticalLeaders(year: number, group: string): Promise<any> {
    return this.request<any>(`/stats/leaders?year=${year}&group=${group}`);
  }

  async getTournamentResults(year: number, group: string): Promise<any> {
    return this.request<any>(`/stats/tournament-results?year=${year}&group=${group}`);
  }

  async getIndividualAwards(year: number, group: string): Promise<any> {
    return this.request<any>(`/stats/special-awards?year=${year}&group=${group}`);
  }

  // Match Galleries
  async getMatchGalleries(page: number = 1, pageSize: number = 12): Promise<GalleryResponse> {
    const params = `?page=${page}&page_size=${pageSize}`;
    return this.request<GalleryResponse>(`/match-galleries${params}`);
  }

  async getMatchPhotos(matchId: string, page: number = 1, pageSize: number = 12): Promise<PhotoResponse> {
    return this.request<PhotoResponse>(`/match-galleries/${matchId}/photos?page=${page}&page_size=${pageSize}`);
  }

  // Event Galleries
  async getEventGalleries(page: number = 1, pageSize: number = 12): Promise<GalleryResponse> {
    const params = `?page=${page}&page_size=${pageSize}`;
    return this.request<GalleryResponse>(`/event-galleries${params}`);
  }

  async getEventPhotos(albumId: string, page: number = 1, pageSize: number = 12): Promise<PhotoResponse> {
    const params = `?page=${page}&page_size=${pageSize}`;
    return this.request<PhotoResponse>(`/event-galleries/${albumId}/photos${params}`);
  }

  // Match Stats Bulk Entry
  async getMatchStatsEntry(matchId: string): Promise<any> {
    return this.request<any>(`/matches/${matchId}/stats-entry`);
  }

  async saveMatchStatsEntry(matchId: string, stats: any[]): Promise<any> {
    const getCookie = (name: string): string | undefined => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop()?.split(';').shift();
      return undefined;
    };

    const csrftoken = getCookie('csrftoken');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (csrftoken) {
      headers['X-CSRFToken'] = csrftoken;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/matches/${matchId}/stats-entry`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ stats }),
        credentials: 'include',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`API save stats failed for match ${matchId}:`, error);
      throw error;
    }
  }

  // Auth Status
  async getUserStatus(): Promise<{ is_authenticated: boolean; is_staff: boolean; username: string | null }> {
    return this.request<{ is_authenticated: boolean; is_staff: boolean; username: string | null }>('/user-status/');
  }

  // Homepage 最新消息
  async getHomeAnnouncement(): Promise<{
    body_text: string;
    link_url: string;
    link_label: string;
    updated_at: string | null;
  }> {
    return this.request<{
      body_text: string;
      link_url: string;
      link_label: string;
      updated_at: string | null;
    }>('/home-announcement');
  }
}

export const apiService = new ApiService(); 
